import { InputHTMLAttributes, ReactNode, forwardRef, useId } from 'react';
import type { BoolTone } from './BoolIndicator';
import { useField, fieldAria } from './FieldContext';
import { HintOverlay } from './HintOverlay';

type BaseProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'aria-label' | 'aria-labelledby'> & {
  /**
   * Visual tone of the dot. Defaults to "purple". Use "green" for
   * Active / status flags so they read as alive rather than configured.
   */
  tone?: BoolTone;
  /**
   * When true, render the muted look but keep the checkbox focusable
   * (i.e. participating in keyboard navigation). Click events are
   * suppressed so the value cannot change.
   */
  readOnly?: boolean;
  /**
   * A short explanation, shown like a FormField's hint while the pointer or
   * the keyboard is on the checkbox; it describes the checkbox to screen
   * readers.
   */
  hint?: ReactNode;
};

// A checkbox must have a name: `label` shows it next to the circle; a table
// cell or a field row passes aria-label (or aria-labelledby) instead. A
// checkbox with neither does not compile.
type NameProps =
  | { label: ReactNode; 'aria-label'?: string; 'aria-labelledby'?: string }
  | { label?: undefined; 'aria-label': string; 'aria-labelledby'?: string }
  | { label?: undefined; 'aria-label'?: string; 'aria-labelledby': string };
export type CheckboxProps = BaseProps & NameProps;

// Modern circular form checkbox. Native <input> is kept (sr-only) for
// keyboard, screen readers and form integration; the visible dot is a
// CSS-only overlay. The original `(e) => e.target.checked` API still
// works — we never replaced the underlying input.
//
// Visual layout:
//   - The label is a flex row that's also `group` and `relative`.
//   - The 20×20 circle, the dash glyph, and the check glyph are all
//     direct siblings of the sr-only `peer` input — required because
//     Tailwind's `peer-*` modifiers only reach direct siblings of `peer`.
//   - The dash and check sit absolutely positioned at the left edge of
//     the label, anchored to overlay the circle (which sits in flow as
//     the first visible child).
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className = '', tone = 'purple', disabled, readOnly, onClick, hint, ...rest },
  ref,
) {
  // Inside a field: the field's id and description; its label only when the
  // checkbox has no visible text of its own.
  const field = useField();
  const token = useId();
  const aria = fieldAria(label != null ? null : field, rest, undefined, token);
  // The hint describes the checkbox, after whatever already does. Its sr-only
  // copy stays outside the <label>, so it never joins the checkbox's name.
  const hintId = `${token}hint`;
  const describedBy = hint
    ? [aria['aria-describedby'] ?? rest['aria-describedby'], hintId].filter(Boolean).join(' ')
    : undefined;
  const fill = tone === 'green'
    ? 'peer-checked:bg-[#10B981] peer-checked:shadow-[0_1px_3px_rgba(16,185,129,0.35),0_0_0_1px_rgba(16,185,129,0.08)]'
    : 'peer-checked:bg-primary peer-checked:shadow-[0_1px_2px_rgba(15,23,42,0.18)]';
  // Solid ring: the circle's own border is 1.48:1, so the ring is the only
  // focus cue and must clear 3:1 (the old /55 alpha was 2.41:1).
  const ring = tone === 'green'
    ? 'peer-focus-visible:ring-[#10B981]'
    : 'peer-focus-visible:ring-primary';

  const box = (
    <label
      className={
        'group relative inline-flex items-center gap-2 text-[0.8125rem] text-slate-700 select-none ' +
        (disabled ? 'opacity-50 cursor-not-allowed ' :
         readOnly ? 'opacity-50 cursor-default ' :
         'cursor-pointer ') +
        className
      }
    >
      <input
        type="checkbox"
        ref={ref}
        disabled={disabled}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        onClick={readOnly ? (e) => { e.preventDefault(); } : onClick}
        className="peer sr-only"
        {...rest}
        {...aria}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
      />
      {/* Circle — flows as the first child so the label keeps its inline width.
          Dash + check are absolutely positioned directly over it below. */}
      <span
        aria-hidden="true"
        className={
          'inline-block shrink-0 w-[18px] h-[18px] rounded-full transition-[background-color,border-color,color,box-shadow,opacity,transform] duration-fast ease-out ' +
          'bg-surface border border-controlBorder ' +
          fill + ' peer-checked:border-transparent ' +
          (disabled || readOnly ? '' : 'group-hover:scale-110 group-active:scale-95 ') +
          'peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 ' + ring
        }
      />
      {/* Off-state dash — centered inside the 18×18 circle (left = (18−6)/2). */}
      <span
        aria-hidden="true"
        className={
          'pointer-events-none absolute left-[6px] top-1/2 -translate-y-1/2 ' +
          'w-[6px] h-px bg-[#94A3B8] transition-opacity duration-150 ' +
          'peer-checked:opacity-0'
        }
      />
      {/* On-state check — same anchor, swaps in via opacity ((18−10)/2). */}
      <svg
        aria-hidden="true"
        width="10" height="10" viewBox="0 0 11 11" fill="none"
        stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
        className={
          'pointer-events-none absolute left-[4px] top-1/2 -translate-y-1/2 ' +
          'opacity-0 transition-opacity duration-150 text-white ' +
          'peer-checked:opacity-100'
        }
      >
        <path d="M2 5.5 L4.5 8 L9 3" />
      </svg>
      {label && <span>{label}</span>}
      {/* As wide as the hint needs (up to max-w-xs), not the label's width. */}
      {hint && <HintOverlay hint={hint} className="w-max" />}
    </label>
  );
  if (!hint) return box;
  return (
    <>
      {box}
      <span id={hintId} className="sr-only">{hint}</span>
    </>
  );
});
